import { useCallback, useState } from "react";

/**
 * Non-sensitive UI preferences only (theme, sidebar state) — the single place that touches
 * localStorage (rules/01 §2: tokens never go here). Storage failures fall back to memory.
 */
export function readPreference<T>(key: string, parse: (raw: string) => T | null): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : parse(raw);
  } catch {
    return null;
  }
}

export function writePreference(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage may be unavailable (private mode); the in-memory value still applies.
  }
}

export function usePreference<T extends string | boolean>(
  key: string,
  initial: T,
  parse: (raw: string) => T | null,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => readPreference(key, parse) ?? initial);
  const set = useCallback(
    (next: T) => {
      setValue(next);
      writePreference(key, String(next));
    },
    [key],
  );
  return [value, set];
}
